import { ref } from "vue";
import { Device } from "mediasoup-client";
import type {
  DtlsParameters,
  Transport,
  Producer,
  RtpCapabilities,
  MediaKind
} from "mediasoup-client/types";

import { useVideoHub } from "@/composables/useVideoHub";
import { producerOptionsVideo } from "@/config";
import { CameraState } from "@/enums/CameraState";
import { VideoState } from "@/types/VideoState";
import { detectPlatform } from "@/scripts/DeviceDetection";

export const videoState = ref<VideoState>(VideoState.NeverConnected);

export function useVideoCall(
  videoId: string
) {

  const videoHub = useVideoHub(videoId);

  let device: Device | undefined;
  let rtpCapabilities: RtpCapabilities | undefined;

  let sendTransport: Transport | undefined;
  let videoProducer: Producer | undefined;
  let statsIntervalId: number | undefined;
  let statsTimeoutId: number | undefined;
  let intentionalTrackStop = false;

  const STATS_COLLECTION_INTERVAL_MS = 5000; // 5 seconds
  const STATS_COLLECTION_DURATION_MS = 60000; // 1 minute

  async function startCall(track: MediaStreamTrack) {
    try {
      setCameraState(CameraState.Enabled); // enabled by default

      videoState.value = VideoState.Connecting;

      const connection = await videoHub.startConnection();

      connection.on("CallerProducerReplaced", () => {
        console.warn("[useVideoCall] Producer replaced by another device");
        videoState.value = VideoState.ReplacedByAnotherDevice;
        cleanup();
      });

      connection.onreconnected(() => {
        console.log("[useVideoCall] SignalR reconnected after network change, reloading page");
        window.location.reload();
      });

      const joinResult = await videoHub.joinRoom();

      console.log("Join Result:", joinResult);

      rtpCapabilities = joinResult?.routerRtpCapabilities;
      if (!rtpCapabilities) {
        throw new Error("Missing RTP capabilities");
      }

      await createDevice();
      await createSendTransport();
      await produceVideo(track);

    } catch (err) {
      console.error("startCall failed", err);
      if (videoState.value !== VideoState.UnsupportedBrowser) {
        videoState.value = VideoState.Failed;
      }
      cleanup();
      throw err;
    }
  }

  async function endCall() {
    await videoHub.disconnect();
    cleanup();
    videoState.value = VideoState.Closed;
  }

  async function createDevice() {
    try {
      device = new Device();

      await device.load({ routerRtpCapabilities: rtpCapabilities! });
    } catch (err: any) {
      if (err?.name === "UnsupportedError") {
        videoState.value = VideoState.UnsupportedBrowser;
      }
      throw err;
    }
  }

  async function createSendTransport() {
    if (!device) {
      throw new Error("Device not initialized");
    }

    const params = await videoHub.createWebRtcTransport();

    sendTransport = device.createSendTransport(params);

    sendTransport.on(
      "connect",
      async ({ dtlsParameters }, callback: any, errback: any) => {
        try {
          await videoHub.connectWebRtcTransport(dtlsParameters);
          callback();
        } catch (err) {
          errback(err as Error);
        }
      }
    );

    sendTransport.on("produce", async (parameters: any, callback: any, errback: any) => {
      try {
        // Detect video rotation for iOS orientation fix
        const currentVideoRotation = detectPlatform() === 'ios' ? 'clockwise' : 'None';
        console.log('currentVideoRotation ' + currentVideoRotation);
        const producerId = await videoHub.produce(
          parameters.kind as MediaKind,
          parameters.rtpParameters,
          currentVideoRotation
        );

        if (!producerId) {
          throw new Error("Producer ID not returned");
        }

        callback({ id: producerId });
      } catch (err) {
        errback(err as Error);
      }
    });

    sendTransport.on("connectionstatechange", (state: RTCPeerConnectionState) => {
      switch (state) {
        case "connecting":
          videoState.value = VideoState.Connecting;
          break;
        case "connected":
          videoState.value = VideoState.Connected;
          break;
        case "failed":
          videoState.value = VideoState.Failed;
          cleanup();
          break;
        case "disconnected":
          videoState.value = VideoState.Disconnected;
          break;
        case "closed":
          videoState.value = VideoState.Closed;
          cleanup();
          break;
      }
    });
  }

  async function produceVideo(track: MediaStreamTrack) {
    if (!sendTransport || !device) {
      throw new Error("Send transport not ready");
    }

    const h264Codec = device.rtpCapabilities.codecs?.find(
      c => c.mimeType.toLowerCase() === 'video/h264'
    );

    const vp8Codec = device.rtpCapabilities.codecs?.find(c => c.mimeType.toLowerCase() === 'video/vp8');
    const selectedCodec = h264Codec ?? vp8Codec;

    if (!selectedCodec) {
      throw new Error("No supported codec is available (H.264 or VP8)");
    }

    console.log(`Producing video with codec ${selectedCodec.mimeType}`);

    videoProducer = await sendTransport.produce({
      track,
      ...producerOptionsVideo,
      codec: selectedCodec
    });

    videoProducer.on("trackended", () => {
      console.warn("Video track ended");
      if (!intentionalTrackStop) {
        cleanup();
      }
    });

    videoProducer.on("transportclose", () => {
      console.warn("Producer transport closed");
      cleanup();
    });

    startStatsCollection();
  }

  async function replaceVideoTrack(track: MediaStreamTrack) {
    if (!videoProducer) {
      return;
    }

    await videoProducer.replaceTrack({ track });
  }

  async function pauseProducer() {
    await setCameraState(CameraState.Disabled);
    videoProducer?.pause();
    videoState.value = VideoState.Paused;
  }

  async function resumeProducer() {
    videoProducer?.resume();
    await setCameraState(CameraState.Enabled);
    videoState.value = VideoState.Connected;
  }

  async function setCameraState(state: CameraState) {
    await videoHub.setCameraState(state);
  }

  function startStatsCollection() {
    if (statsIntervalId) return;

    console.log("Producer stats collection started (1 minute)");

    statsIntervalId = window.setInterval(async () => {
      if (sendTransport) {
        try {
          const stats = await (sendTransport as any)._handler._pc.getStats();
          const statsObj: any = {};
          stats.forEach((report: any) => {
            statsObj[report.id] = report;
          });
          await videoHub.sendProducerStats(statsObj);
        } catch (err) {
          console.error("Failed to collect producer stats:", err);
        }
      }
    }, STATS_COLLECTION_INTERVAL_MS);

    // Stop after 1 minute
    statsTimeoutId = window.setTimeout(() => {
      console.log("Producer stats collection timeout reached");
      stopStatsCollection();
    }, STATS_COLLECTION_DURATION_MS);
  }

  function stopStatsCollection() {
    if (statsIntervalId) {
      clearInterval(statsIntervalId);
      statsIntervalId = undefined;
      console.log("Producer stats collection stopped");
    }

    if (statsTimeoutId) {
      clearTimeout(statsTimeoutId);
      statsTimeoutId = undefined;
    }
  }

  function cleanup() {
    stopStatsCollection();
    try { videoProducer?.close(); } catch {}
    try { sendTransport?.close(); } catch {}

    device = undefined;
    sendTransport = undefined;
    videoProducer = undefined;
    rtpCapabilities = undefined;
  }

  function setIntentionalTrackStop(value: boolean) {
    intentionalTrackStop = value;
  }

  function isActive(): boolean {
    return !!videoProducer && !!sendTransport;
  }

  return {
    startCall,
    endCall,
    replaceVideoTrack,
    pauseProducer,
    resumeProducer,
    setIntentionalTrackStop,
    isActive,
    videoState
  };
}
